import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { B2BAllowedAuthMethods } from '@stytch/react/b2b';
import { useParams } from 'react-router';

import { useOrganizationBySlug } from '@/api/organization';
import PasswordSignInForm from '@/app/sign-in/PasswordSignInForm';
import SignInPanel from '@/app/sign-in/SignInPanel';
import { PageSpinner } from '@/components/loaders';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { setTenantPreferredSignInMethod } from '@/helper/user-preferences';
import useDeferredLoading from '@/hooks/useDeferredLoading';
import { ROUTES } from '@/router';

import InstanceError from '../InstanceError';
import TenantAuthenticate from '../TenantAuthenticate';

const InstanceSignInPassword = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { organization, isLoading } = useOrganizationBySlug(orgSlug);
  const showLoading = useDeferredLoading(isLoading);

  const instanceSignInPath = ROUTES.signInInst.path.replace(':orgSlug', orgSlug ?? '');
  const forgotPasswordPath = ROUTES.signInInstPasswordForgot.path.replace(
    ':orgSlug',
    orgSlug ?? '',
  );

  if (!orgSlug) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={<Trans>No instance specified.</Trans>}
      />
    );
  }

  if (isLoading && showLoading) {
    return <PageSpinner />;
  }

  if (isLoading) {
    return null;
  }

  if (!organization) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={
          <Trans>The instance "{orgSlug}" was not found or you don't have access to it.</Trans>
        }
      />
    );
  }

  // Check if password is allowed for this organization
  const allowedMethods: B2BAllowedAuthMethods[] = organization.allowed_auth_methods || [];
  const authMethodsMode = organization.auth_methods;
  const isPasswordAllowed =
    authMethodsMode === 'ALL_ALLOWED' || allowedMethods.includes('password');

  if (!isPasswordAllowed) {
    return (
      <InstanceError
        title={t`Password Sign In Not Available`}
        message={
          <Trans>
            Password sign-in is not enabled for {organization.organization_name}. Please use another
            sign-in method.
          </Trans>
        }
      />
    );
  }

  return (
    <FullPage title={t`Sign In with Password`}>
      <TenantAuthenticate orgSlug={orgSlug}>
        <SignInPanel
          title={t`Sign In to ${organization.organization_name}`}
          footer={
            <>
              <Text variant="caption1" color="grey">
                <Trans>
                  Forgot your password?{' '}
                  <Link variant="caption1" color="aqua" to={forgotPasswordPath}>
                    {t`Reset your password`}
                  </Link>
                </Trans>
              </Text>
              <Text variant="caption1" color="grey">
                <Trans>
                  Prefer to not use a password?{' '}
                  <Link variant="caption1" color="aqua" to={instanceSignInPath}>
                    Sign in another way
                  </Link>
                </Trans>
              </Text>
            </>
          }
          showGap
        >
          <PasswordSignInForm
            organizationId={organization.organization_id}
            onSuccess={() => {
              if (orgSlug) {
                setTenantPreferredSignInMethod(orgSlug, 'email');
              }
            }}
          />
        </SignInPanel>
      </TenantAuthenticate>
    </FullPage>
  );
};

export default InstanceSignInPassword;
